import React from 'react';

import {useSelector} from 'react-redux';

import {
  GenericSidebarItemType,
  RTKGenericAttachedItemsSidebar,
} from 'app/components/general/GenericAttachedItemsSidebar/RTKGenericAttachedItemsSidebar';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {CurrentUserSelector} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {keepUpdatedEnhancer} from 'app/general/keepUpdatedEnhancer';
import {useAddQuizLink, useRemoveQuizLink} from 'app/quizzes/api';
import {sweetAPI} from 'app/store/sweet-api';
import {runWhenAllRelatedMatch} from 'app/subscriptions';
import {getContentType} from 'app/utils/content-type';
import {getObjectName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {
  POLICY_RTK_TAGS,
  PROCEDURE_RTK_TAGS,
  PROCESS_RTK_TAGS,
} from 'app/utils/rtk-tags';

import type {UseQueryType} from 'app/general/datafetchers/hooks/useInfiniteListFetcherRTK';
import type {AttachedItemType} from 'app/quizzes/types';
import type {
  ModelChangeMessageType,
  ModelSubscribedComponentPropsType,
} from 'app/subscriptions';

type Props = {
  quizURL: string;
  quizId: string;
  canEdit: boolean;
  contentType: 'procedure' | 'proceduregroup' | 'policy';
  attachedItems: Array<GenericSidebarItemType>;
  createURL: string;
  onClose: () => void;
  onModelChange: () => void;
  useDataHook: UseQueryType<any, any>;
  paramsConverter: (query: Record<string, unknown>) => any;
} & ModelSubscribedComponentPropsType;

export const QuizGenericObjectsSidebar = keepUpdatedEnhancer<Props>(
  ({quizURL, contentType, canEdit, attachedItems, ...other}) => {
    const {isTeamMember, isManager} = getCurrentUserPerms(
      useSelector((state) => CurrentUserSelector(state)?.data)
    );
    const [addQuizLink] = useAddQuizLink();
    const [removeQuizLink] = useRemoveQuizLink();

    const actions = useAsyncActions({
      invalidateTags: sweetAPI.util.invalidateTags,
    });

    const ct = getContentType(contentType);
    const onRemove = (item: AttachedItemType) => {
      return removeQuizLink({url: item.link_url}).unwrap();
    };
    const onAttach = (item: AttachedItemType) => {
      return addQuizLink({
        quiz: quizURL,
        document: item.url,
      }).unwrap();
    };
    const props = {
      attachedItems,
      title: gt('Related %s', ct.pluralName),
      itemComponentProps: (item, attached) => {
        return {
          disableAndShowTooltipText: !canEdit
            ? gt(
                'You have to be a %s manager to add or remove quizzes',
                ct.name
              )
            : undefined,
          actionTooltip: attached
            ? gt('Remove quiz from this %s', ct.name)
            : gt('Add quiz to this %s', ct.name),
        };
      },
      itemNameProcessor: (object) => getObjectName(object, contentType),
      handleModelChange: (message: ModelChangeMessageType) => {
        const ct = contentType === 'proceduregroup' ? 'process' : contentType;
        runWhenAllRelatedMatch(
          message,
          `quiz${ct}`,
          {
            content_type: 'quiz',
            id: parseInt(other.quizId),
          },
          () => {
            actions.invalidateTags([
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: POLICY_RTK_TAGS.TAGS, id: 'list'},
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: PROCESS_RTK_TAGS.TAGS, id: 'list'},
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'},
            ]);
          }
        );
      },
      onRemove,
      emptyResultsComponents: (
        <NoResultsMessage
          onSidebar={true}
          icon="fa-file"
          head={gt('No %s', ct.pluralName)}
        >
          {isManager
            ? gt(
                'You can attach this quiz to existing %(pluralName)s, detach it from a %(name)s or create a new %(name)s using the button below',
                ct
              )
            : null}
        </NoResultsMessage>
      ),

      showFilterField: isManager,
      searchPlaceholder: gt('Search %s...', ct.pluralName),
      onAttach,

      canCreate: isTeamMember,
      createLabel: gt('Create %s', ct.name),

      ...other,
    };
    return <RTKGenericAttachedItemsSidebar {...props} />;
  }
);
